import * as React from "react";
import { formatToApiDate } from "@repo/utils";
import {
  getReminders,
  createReminder as apiCreateReminder,
  updateReminder as apiUpdateReminder,
  deleteReminder as apiDeleteReminder,
  completeReminder,
} from "@/lib/api/reminders";
import { logger } from "@/lib/utils/logger";

export type ReminderVisibility = "personal" | "pharmacy";
export type ReminderPriority = "standard" | "urgent";
export type ReminderReferenceType =
  | "inventory"
  | "receipt"
  | "dispense"
  | "adjustment";
export type ReminderRecurrenceType =
  | "none"
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly";

export interface Reminder {
  id: string;
  pharmacy_id: string;
  title: string;
  description: string | null;
  visibility: ReminderVisibility;
  priority: ReminderPriority;
  due_date: string;
  due_time: string | null;
  reference_type: ReminderReferenceType | null;
  reference_id: string | null;
  recurrence_type: ReminderRecurrenceType;
  recurrence_interval: number;
  recurrence_end_date: string | null;
  recurrence_parent_id: string | null;
  is_completed: boolean;
  completed_by: string | null;
  completed_at: string | null;
  completion_note: string | null;
  patient_id: string | null;
  patient_name: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  created_by_user: {
    id: string;
    full_name: string;
    initials: string;
  } | null;
  completed_by_user: {
    id: string;
    full_name: string;
    initials: string;
  } | null;
}

export interface ReminderFilters {
  visibility?: ReminderVisibility | "all";
  status?: "active" | "completed" | "all";
  priority?: ReminderPriority | "all";
  patientLinked?: boolean;
}

export interface CreateReminderInput {
  title: string;
  description?: string;
  visibility?: ReminderVisibility;
  priority?: ReminderPriority;
  due_date: string;
  due_time?: string;
  reference_type?: ReminderReferenceType;
  reference_id?: string;
  patient_id?: string;
  recurrence_type?: ReminderRecurrenceType;
  recurrence_interval?: number;
  recurrence_end_date?: string;
}

export interface UpdateReminderInput {
  title?: string;
  description?: string | null;
  visibility?: ReminderVisibility;
  priority?: ReminderPriority;
  due_date?: string;
  due_time?: string | null;
  reference_type?: ReminderReferenceType | null;
  reference_id?: string | null;
  patient_id?: string | null;
  recurrence_type?: ReminderRecurrenceType;
  recurrence_interval?: number;
  recurrence_end_date?: string | null;
}

export interface UseRemindersReturn {
  reminders: Reminder[];
  isLoading: boolean;
  error: string | null;
  createReminder: (input: CreateReminderInput) => Promise<Reminder | null>;
  updateReminder: (
    id: string,
    input: UpdateReminderInput
  ) => Promise<Reminder | null>;
  deleteReminder: (id: string) => Promise<boolean>;
  toggleComplete: (
    id: string,
    completionNote?: string
  ) => Promise<Reminder | null>;
  refetch: () => Promise<void>;
  // Computed
  overdueCount: number;
  dueTodayCount: number;
  activeCount: number;
}

/**
 * Hook for fetching and managing reminders.
 */
export function useReminders(): UseRemindersReturn {
  const [reminders, setReminders] = React.useState<Reminder[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const fetchingRef = React.useRef(false);

  const fetchReminders = React.useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const responseData = await getReminders();
      setReminders(
        (responseData as { data: Reminder[] }).data ||
          (responseData as Reminder[])
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to fetch reminders";
      setError(message);
      logger.error("Fetch reminders failed", err, {
        operation: "fetch_reminders",
      });
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  // Auto-load on mount
  React.useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const createReminderFn = React.useCallback(
    async (input: CreateReminderInput): Promise<Reminder | null> => {
      try {
        const responseData = await apiCreateReminder(input);
        const newReminder =
          (responseData as { data: Reminder }).data ||
          (responseData as Reminder);

        // Refetch to ensure correct ordering
        await fetchReminders();
        return newReminder;
      } catch (err) {
        logger.error("Create reminder failed", err, {
          operation: "create_reminder",
        });
        return null;
      }
    },
    [fetchReminders]
  );

  const updateReminderFn = React.useCallback(
    async (
      id: string,
      input: UpdateReminderInput
    ): Promise<Reminder | null> => {
      try {
        const responseData = await apiUpdateReminder(id, input);
        const updated =
          (responseData as { data: Reminder }).data ||
          (responseData as Reminder);

        // Optimistic update
        setReminders((prev) => prev.map((r) => (r.id === id ? updated : r)));

        return updated;
      } catch (err) {
        logger.error("Update reminder failed", err, {
          operation: "update_reminder",
          metadata: { reminderId: id },
        });
        return null;
      }
    },
    []
  );

  const deleteReminderFn = React.useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await apiDeleteReminder(id);

        // Optimistic update
        setReminders((prev) => prev.filter((r) => r.id !== id));
        return true;
      } catch (err) {
        logger.error("Delete reminder failed", err, {
          operation: "delete_reminder",
          metadata: { reminderId: id },
        });
        await fetchReminders();
        return false;
      }
    },
    [fetchReminders]
  );

  const toggleComplete = React.useCallback(
    async (id: string, completionNote?: string): Promise<Reminder | null> => {
      try {
        const responseData = await completeReminder(id, {
          notes: completionNote,
        });
        const { reminder: updated, nextReminder } =
          (
            responseData as {
              data: { reminder: Reminder; nextReminder?: Reminder };
            }
          ).data ||
          (responseData as { reminder: Reminder; nextReminder?: Reminder });

        setReminders((prev) => {
          const list = prev.map((r) => (r.id === id ? updated : r));
          if (nextReminder) list.unshift(nextReminder);
          return list;
        });

        return updated;
      } catch (err) {
        logger.error("Toggle reminder complete failed", err, {
          operation: "toggle_reminder",
          metadata: { reminderId: id },
        });
        await fetchReminders();
        return null;
      }
    },
    [fetchReminders]
  );

  // Computed values
  const today = formatToApiDate(new Date());
  const overdueCount = reminders.filter(
    (r) => !r.is_completed && r.due_date < today
  ).length;
  const dueTodayCount = reminders.filter(
    (r) => !r.is_completed && r.due_date === today
  ).length;
  const activeCount = reminders.filter((r) => !r.is_completed).length;

  return {
    reminders,
    isLoading,
    error,
    createReminder: createReminderFn,
    updateReminder: updateReminderFn,
    deleteReminder: deleteReminderFn,
    toggleComplete,
    refetch: fetchReminders,
    overdueCount,
    dueTodayCount,
    activeCount,
  };
}
